import { useCallback, useMemo } from 'react';
import { useAuth } from '../auth/useAuth';
import { isRedirectInProgress } from '../auth/redirectInProgress';
import { config } from '../config';

const API_BASE = config.apiBaseUrl;

export function useApi() {
  const { getAccessToken, handleSessionExpired } = useAuth();

  const apiFetch = useCallback(async (path: string, options?: RequestInit & { externalSignal?: AbortSignal; timeoutMs?: number }) => {
    // Combine the caller's optional cancellation signal with our own 30s timeout (Story #24793: a
    // caller may lengthen it via `timeoutMs` — the PRISM seat PUT waits up to 2 minutes server-side).
    // Whichever fires first aborts the underlying fetch — fixes ERR_INSUFFICIENT_RESOURCES
    // when callers stack requests on rapid navigation (#7040). Factored out so both the
    // initial attempt and the one 401 retry (below) get their own fresh timeout window.
    const doFetch = async (token: string): Promise<Response> => {
      const timeoutController = new AbortController();
      const timeout = setTimeout(() => timeoutController.abort(), options?.timeoutMs ?? 30000);
      const signals: AbortSignal[] = [timeoutController.signal];
      if (options?.externalSignal) signals.push(options.externalSignal);
      const signal = signals.length > 1 ? AbortSignal.any(signals) : signals[0];
      try {
        const { externalSignal: _drop, timeoutMs: _dropTimeout, ...rest } = options ?? {};
        void _drop;
        void _dropTimeout;
        // Story #13224 — a multipart upload must NOT carry an explicit Content-Type: the browser
        // sets `multipart/form-data` together with the boundary token it generates, and a
        // hand-written header overwrites the boundary, leaving the server unable to parse a body
        // it has just been told the shape of. So the JSON default applies only when the body is
        // not FormData.
        const isFormData = typeof FormData !== 'undefined' && rest.body instanceof FormData;
        return await fetch(`${API_BASE}${path}`, {
          ...rest,
          signal,
          headers: {
            'Authorization': `Bearer ${token}`,
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            ...options?.headers,
          },
        });
      } finally {
        clearTimeout(timeout);
      }
    };

    // #14687 — getAccessToken now THROWS RedirectInProgressError once it has started an
    // interactive redirect, instead of returning ''. Let it propagate: the page is navigating to
    // Entra, this request is moot, and calling handleSessionExpired() here is precisely what
    // produced the second competing navigation.
    const token = await getAccessToken();
    let res = await doFetch(token);

    // 401 = token expired/rejected (403 is authorized-but-forbidden, handled by callers
    // and never retried/logged-out here). Give the session exactly one chance to recover
    // before treating this as a genuine expiry: force a fresh token and retry the request
    // once. Only if the retry ALSO 401s (or the forced re-acquire itself throws/redirects)
    // do we treat it as a real session expiry — a single transient 401 must not immediately
    // log the operator out (#13569).
    if (res.status === 401) {
      try {
        const freshToken = await getAccessToken(true);
        res = await doFetch(freshToken);
      } catch (error) {
        // #14687 — distinguish "navigating away to re-authenticate" from "the re-acquire genuinely
        // failed". The sentinel means a redirect is already in flight; logging out on top of it is
        // the bug. Rethrow so the caller aborts, and leave the redirect to complete.
        if (isRedirectInProgress(error)) throw error;
        handleSessionExpired();
        return res;
      }
      if (res.status === 401) handleSessionExpired();
    }
    return res;
  }, [getAccessToken, handleSessionExpired]);

  const get = useCallback(async <T>(path: string, signal?: AbortSignal): Promise<T> => {
    const res = await apiFetch(path, { externalSignal: signal });
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    return res.json();
  }, [apiFetch]);

  // Story #13890 (S4) — Account Comms .eml viewer: fetches raw non-JSON content (the streamed
  // `message/rfc822` body) as text, for client-side parsing/rendering — unlike `downloadBlob`,
  // this does NOT trigger a browser save-as; it returns the text to the caller.
  const getText = useCallback(async (path: string, signal?: AbortSignal): Promise<string> => {
    const res = await apiFetch(path, { externalSignal: signal });
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    return res.text();
  }, [apiFetch]);

  const post = useCallback(async <T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> => {
    const res = await apiFetch(path, { method: 'POST', body: JSON.stringify(body), externalSignal: signal });
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    return parseBody<T>(res);
  }, [apiFetch]);

  /**
   * Story #13224 — multipart POST, for the staged attachment upload on the New Case form.
   *
   * Separate from `post` rather than an overload of it: `post` JSON-serialises its body and sets
   * the JSON content type, both of which are wrong for a file. Uploads go through the API exactly
   * like downloads do — never a browser-held SAS (#14781's rule) — so this is the only path a
   * file takes out of the portal.
   */
  const postForm = useCallback(async <T>(path: string, form: FormData, signal?: AbortSignal): Promise<T> => {
    const res = await apiFetch(path, { method: 'POST', body: form, externalSignal: signal });
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    return parseBody<T>(res);
  }, [apiFetch]);

  /**
   * `requestOptions.timeoutMs` (Story #24793) lengthens the default 30s timeout for a PUT the server
   * deliberately holds open — the PRISM seat change re-reads PRISM for up to 2 minutes. Omitted, the
   * behaviour is unchanged.
   */
  const put = useCallback(async <T>(path: string, body: unknown, signal?: AbortSignal, requestOptions?: { timeoutMs?: number }): Promise<T> => {
    const res = await apiFetch(path, { method: 'PUT', body: JSON.stringify(body), externalSignal: signal, timeoutMs: requestOptions?.timeoutMs });
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    return parseBody<T>(res);
  }, [apiFetch]);

  // PATCH for partial updates. Tolerates 204 No Content (and empty bodies) — many of our
  // mutate endpoints return 204 on success, so an unconditional res.json() would throw on
  // the empty body. Returns null when there is no response body.
  const patch = useCallback(async <T>(path: string, body: unknown, signal?: AbortSignal): Promise<T | null> => {
    const res = await apiFetch(path, { method: 'PATCH', body: JSON.stringify(body), externalSignal: signal });
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    if (res.status === 204) return null;
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : null;
  }, [apiFetch]);

  const del = useCallback(async (path: string, signal?: AbortSignal): Promise<void> => {
    const res = await apiFetch(path, { method: 'DELETE', externalSignal: signal });
    if (!res.ok && res.status !== 204) throw new ApiError(res.status, await res.json().catch(() => ({})));
  }, [apiFetch]);

  const downloadBlob = useCallback(async (path: string, fileName: string): Promise<void> => {
    const res = await apiFetch(path);
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  }, [apiFetch]);

  // #12192 — fetch a binary body and hand back an object URL for INLINE display (the proposal
  // builder's PDF preview pane). Distinct from `downloadBlob`, which triggers a save and revokes
  // immediately: an <iframe src> needs the URL to stay alive, and it cannot carry the bearer token
  // itself, so the authenticated fetch has to happen here. **The caller owns the URL and must
  // `URL.revokeObjectURL` it** when the preview closes or is replaced — otherwise the blob is
  // retained for the life of the document.
  const getObjectUrl = useCallback(async (path: string): Promise<string> => {
    const res = await apiFetch(path);
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    return URL.createObjectURL(await res.blob());
  }, [apiFetch]);

  // Stable object reference. Without useMemo this hook returns a fresh object literal
  // every render — pages doing `const api = useApi(); ... useEffect(()=>fetch, [api])`
  // re-fire their effect on every render and stack requests until Chrome's connection
  // pool exhausts (ERR_INSUFFICIENT_RESOURCES, #7040 root cause).
  return useMemo(
    () => ({ get, post, postForm, put, patch, del, downloadBlob, getObjectUrl, getText }),
    [get, post, postForm, put, patch, del, downloadBlob, getObjectUrl, getText],
  );
}

// Tolerate empty success bodies: an action endpoint may return Ok() with no JSON, and
// res.json() throws SyntaxError on an empty body. Returns undefined for an empty body so
// callers awaiting a no-content POST/PUT don't get an unhandled rejection. (#10462 review fix.)
async function parseBody<T>(res: Response): Promise<T> {
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// Bug #16198 — statuses that are a NORMAL part of using the app rather than a fault, and so
// deserve a sentence a user can act on. An ASP.NET `[Authorize]` rejection has an EMPTY body, so
// there is no ProblemDetails `detail` to fall back to and the message rendered literally as
// "API error 403" — which is what two internal teams met on the platform's default landing screen.
//
// Scoped deliberately to 401/403: every other status keeps `API error ${status}`, because a 500 is
// a fault and dressing it up as a friendly sentence would hide it. A server-supplied `detail`
// still wins over both — it is more specific than anything generic we can write here.
const DESIGNED_STATUS_MESSAGES: Record<number, string> = {
  401: 'Your session has expired. Sign in again to continue.',
  403: "You don't have access to this. It belongs to a team surface you're not a member of — ask an administrator if you need access.",
};

export class ApiError extends Error {
  constructor(public status: number, public body: Record<string, unknown>) {
    super((body?.detail as string) || DESIGNED_STATUS_MESSAGES[status] || `API error ${status}`);
  }
}
